import { useReducedMotion } from 'framer-motion';
import { OTTO_ANIMATION_DURATION, OTTO_ANIMATION_INK, OTTO_ANIMATION_SHAPES, OTTO_ANIMATION_VIEWBOX } from './otto-animation-model';

/** Atributos de forma do arquivo de design (kebab) nos nomes que o React espera. */
const REACT_ATTR = Object.freeze({
    'fill-rule': 'fillRule',
    'stroke-width': 'strokeWidth',
    'stroke-linecap': 'strokeLinecap',
    'stroke-linejoin': 'strokeLinejoin',
    'stroke-dasharray': 'strokeDasharray',
});

const inkOf = (value, ink) => (typeof value === 'string' ? value.split(OTTO_ANIMATION_INK).join(ink) : value);

function reactAttrs(attrs, ink) {
    return Object.fromEntries(Object.entries(attrs).map(([name, value]) => [REACT_ATTR[name] ?? name, inkOf(value, ink)]));
}

function Animations({ animations, duration, ink }) {
    return animations.map((animation) => (
        <animate
            key={animation.attr}
            attributeName={animation.attr}
            dur={`${duration}s`}
            repeatCount="indefinite"
            // Sem curva no desenho (a barra da dobra só liga e desliga): linear, que é o padrão do SMIL
            calcMode={animation.keySplines ? 'spline' : undefined}
            keyTimes={animation.keyTimes}
            keySplines={animation.keySplines ?? undefined}
            values={inkOf(animation.values, ink)}
        />
    ));
}

function Shape({ shape, duration, ink, animated }) {
    const Tag = shape.tag;
    const motion = animated ? <Animations animations={shape.animations} duration={duration} ink={ink} /> : null;
    return (
        <Tag {...reactAttrs(shape.attrs, ink)}>
            {motion}
            {(shape.children ?? []).map((child) => (
                <path key={child.d} {...reactAttrs(child, ink)} />
            ))}
        </Tag>
    );
}

/**
 * A animação do Otto: o logotipo "otto" se deforma até virar o ícone e volta, em ciclo (a ordem é: o ponto grava e encolhe,
 * o balão se fecha, o primeiro t se desdobra em três linhas e a barra recolhe e vira a lente da câmera).
 *
 * - `ink`: a cor das letras. O arquivo de design usa `#0F1115`; no fundo escuro passe `#FFFFFF`. No meio do ciclo as peças
 *   ficam brancas sobre o azul do ícone, nos dois temas. É um valor literal (e não `currentColor`) porque a cor também é
 *   animada, e o `currentColor` dentro de `<animate>` não vale em todos os navegadores.
 * - `duration`: segundos de um ciclo (5 no desenho).
 * - Movimento reduzido (sistema) ou `animated={false}`: fica parado no logotipo, sem nenhum `<animate>`.
 * - Sem `<style>`: nada aqui vaza para outros SVGs da página.
 *
 * `role="img"` com `label` como nome acessível; passe `decorative` quando o texto ao lado já diz o que é.
 */
export function OttoAnimation({ ink = OTTO_ANIMATION_INK, duration = OTTO_ANIMATION_DURATION, animated = true, decorative = false, label = 'Otto', className, ...props }) {
    const reduced = useReducedMotion();
    const moving = animated && !reduced;
    const a11y = decorative ? { 'aria-hidden': 'true', focusable: 'false' } : { role: 'img', 'aria-label': label };

    return (
        <svg viewBox={OTTO_ANIMATION_VIEWBOX} className={className} data-otto-animation={moving ? 'moving' : 'still'} {...a11y} {...props}>
            {OTTO_ANIMATION_SHAPES.map((shape, index) => (
                <Shape key={index} shape={shape} duration={duration} ink={ink} animated={moving} />
            ))}
        </svg>
    );
}

export default OttoAnimation;
